import React from 'react'
import 'src/assets/css/components/runHistoryIndicator.css'
import { JobStatus } from 'src/models/IJob'

/**
 * The last few run attempts as coloured dots (MEL-657) — the evidence behind the stability verdict.
 * On an unstable pipeline it is why "Unstable" is not a claim the user has to take on trust; on a
 * stable one it is a row of green ticks that says, at a glance, this pipeline is healthy. Shown for
 * both — the caller decides which pipelines have a history worth drawing (see `DataRow`).
 *
 * Three states, because retries count towards the verdict and a user still has to be able to tell
 * them apart: green for an attempt that passed, red for one that failed for good, **amber for one
 * that failed and was retried** (WARNING). Collapsing the amber ones into the run that eventually
 * succeeded was what hid the instability the chip is about; showing them as red instead would say
 * the pipeline never delivered that run, which is not true either.
 *
 * Two things the dots must not rely on (QA round 2):
 * - **Order is oldest → newest, left to right.** The backend hands them over newest-first, and
 *   rendering them in that order read backwards to everyone who looked at it: a strip of runs is
 *   read as a timeline, so the most recent run belongs at the *end*, next to the latest-run chip.
 * - **Colour is never the only signal.** Each dot carries a tick, a cross or an exclamation mark
 *   and names itself on hover, so the strip is legible to a red/green colour-blind reader (~8% of
 *   men) and in greyscale.
 */

interface IRunHistoryIndicatorProps {
    /** Outcomes of the recent attempts, **newest first** (as the payload gives them). */
    runs: JobStatus[]
}

/** How one attempt reads: its dot colour, its glyph, and what it says on hover. */
const appearance = (run: JobStatus) => {
    if (run === JobStatus.Error)
        return { state: 'failed', glyph: 'fa-times', title: 'Failed' }

    if (run === JobStatus.Warning)
        return { state: 'retried', glyph: 'fa-exclamation', title: 'Failed, retried' }

    return { state: 'succeeded', glyph: 'fa-check', title: 'Succeeded' }
}

/** A retried attempt failed, so it counts here exactly as an outright failure does. */
const isFailure = (run: JobStatus): boolean =>
    run === JobStatus.Error || run === JobStatus.Warning

const RunHistoryIndicator = (props: IRunHistoryIndicatorProps): JSX.Element | null => {
    if (!props.runs.length)
        return null

    const failures = props.runs.filter(isFailure).length
    // `slice` first: `reverse` mutates, and this array belongs to the pipeline in the caller's state
    const oldestFirst = props.runs.slice().reverse()

    return <span
        className='run-history'
        // the dots are decorative on their own; the title is what makes them readable
        title={`Last ${props.runs.length} ${props.runs.length === 1 ? 'run' : 'runs'}, oldest first`}
        aria-label={`${failures} of the last ${props.runs.length} runs failed`}>
        {oldestFirst.map((run, i) => {
            const { state, glyph, title } = appearance(run)

            return <span
                key={i}
                className={`run-history-dot ${state}`}
                title={title}>
                <i className={`fas ${glyph}`} />
            </span>
        })}
    </span>
}

export default RunHistoryIndicator
